import React, { Component } from "react";
import {
    Box,
    FormGroup,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    Typography,
    FormControl,
    Autocomplete,
    TextField
} from "@mui/material";
import AlertDialogPopop from "../../Component/AlertDialogPopop/AlertDialogPopop";
import InputTextField from "../../Component/Reusables/InputTextField";
import { ShowAlert } from "../../Component/ShowAlert";
import { formatLimit, convertToNumber } from '../../Component/config';
import { amountFormat } from '../../Component/helper';
import { loadFromLocalStorage } from "../../Common/Validation/LocalStorage";
import { SyncContext, SyncContextType } from "../../Contexts/BatchSyncContext";
import { AddReferralBonusCreditsProps, AddReferralBonusCreditsState, RulePair, RulePairError } from "./Interface";
import ReferralService from "../../Services/ReferralService";

// Main class for managing referral settings
class AddReferralBonusCredits extends Component<AddReferralBonusCreditsProps, AddReferralBonusCreditsState> {
    entryRefs: React.RefObject<HTMLDivElement>[] = [];
    static contextType = SyncContext;
    context!: SyncContextType; //

    constructor(props: AddReferralBonusCreditsProps) {
        super(props);
        this.state = {
            accessToken: "",
            allRules: [],
            commonSettings: {
                commonSettingsId: "",
                currencyCode: "USD",
                countryName: "",
                currencySymbol: "",
                currencyEntity: "",
                referrermaxEarnings: "0.00",
                defaultReferrerBonus: "0.00",
                defaultRefereeBonus: "0.00",
                refereeMinTxn: "0.00",
            },
            defaultCurrencySymbol: "$",
            errors: {
                ruleName: "",
                ruleDescription: "",
                rulePairs: [],
            },
            isSaveConfirmationDialogOpen: false,
            referrerMaxEarningsList: [],
            ruleDescription: "",
            ruleName: "",
            rulePairs: [],
            selectedRule: null,
            selectedRuleName: "",
            selectedRuleDescription: "",
            selectedRuleId: ""
        };
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();

            this.setState({ accessToken }, async () => {
                this.getInitialData()
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    // Fetch referral entries...
    private async getInitialData() {
        try {
            const response = await ReferralService.getAllAvilableRulesWithCommonData(this.state.accessToken);

            if (!response.ok) {
                ShowAlert("Error", "Failed to fetch initial data. Please try again later.", "error");
                return;
            }

            const data = await response.json();
            this.setState({
                referrerMaxEarningsList: data.commonSettings || [],
                allRules: data.rules || [],
            }, () => this.updateCommonReferrerLimitOnMount());

        } catch (error) {
            console.error("Error fetching referrals:", error);
            ShowAlert("Error", "An unexpected error occurred while fetching referral entries.", "error");
        }
    }

    // Update maximum potential earnings....
    updateCommonReferrerLimitOnMount = () => {
        const { referrerMaxEarningsList } = this.state
        const { currencyCode } = this.state.commonSettings

        const selectedSenderCurrency = referrerMaxEarningsList.find(
            (item) => item.currencyCode === currencyCode
        );

        if (selectedSenderCurrency) {
            this.setState({
                commonSettings: selectedSenderCurrency
            });
        }
    };

    validateFields = (): boolean => {
        const { selectedRuleName, selectedRuleDescription, rulePairs } = this.state;

        let isValid = true;

        const ruleNameError = selectedRuleName.trim() ? "" : "Select a rule or create a new one.";
        const ruleDescriptionError = selectedRuleDescription.trim() ? "" : "Rule description is required.";

        if (ruleNameError || ruleDescriptionError) {
            isValid = false;
        }

        const rulePairErrors: RulePairError[] = rulePairs.map((entry, index) => {
            const entryErrors: RulePairError = {
                referrerBonusCredit: "",
                refereeCurrency: "",
                refereeBonusCredit: "",
                refereeMinTransaction: "",
            };
            // Referrer Bonus Credit
            const referrerRaw = entry.referrerBonusCredit?.toString().replace(/,/g, "") || "";
            const referrerBonus = parseFloat(referrerRaw);

            const referrerMinTxnRaw = entry.refereeMinTransaction?.toString().replace(/,/g, "") || "";
            const referrerMinTxn = parseFloat(referrerMinTxnRaw);

            if (
                isNaN(referrerBonus) ||
                referrerBonus <= 0 ||
                isNaN(referrerMinTxn) ||
                referrerBonus > referrerMinTxn
            ) {
                entryErrors.referrerBonusCredit = "Bonus must be greater than 0 and less than or equal to Min Transaction amount";
                isValid = false;
            }

            // Referee Bonus Credit
            const refereeRaw = entry.refereeBonusCredit?.toString().replace(/,/g, "") || "";
            const refereeBonus = parseFloat(refereeRaw);

            const minTxnRaw = entry.refereeMinTransaction?.toString().replace(/,/g, "") || "";
            const minTxn = parseFloat(minTxnRaw);

            if (
                isNaN(refereeBonus) ||
                refereeBonus <= 0 ||
                isNaN(minTxn) ||
                refereeBonus > minTxn
            ) {
                entryErrors.refereeBonusCredit = "Bonus must be greater than 0 and less than or equal to Min Transaction amount";
                isValid = false;
            }

            // Referee Minimum Transaction
            // const minTxnRaw = entry.refereeMinTransaction?.toString().replace(/,/g, "") || "";
            // const minTxn = parseFloat(minTxnRaw);
            if (isNaN(minTxn) || minTxn <= 0) {
                entryErrors.refereeMinTransaction = "Minimum transaction is required.";
                isValid = false;
            }

            return entryErrors;
        });

        this.setState({
            errors: {
                ruleName: ruleNameError,
                ruleDescription: ruleDescriptionError,
                rulePairs: rulePairErrors,
            },
        });

        return isValid;
    };

    // Handle save button click...
    handleSave = async () => {

        const {
            rulePairs,
            commonSettings,
            selectedRuleName,
            selectedRuleDescription,
            accessToken,
            selectedRuleId
        } = this.state;

        if (selectedRuleName !== "") {
            if (!this.validateFields()) return;
        }

        try {
            // Format entries before saving
            const formattedEntries = rulePairs.map(entry => ({
                ...entry,
                referrerBonusCredit: convertToNumber(entry.referrerBonusCredit),
                refereeBonusCredit: convertToNumber(entry.refereeBonusCredit),
                refereeMinTransaction: convertToNumber(entry.refereeMinTransaction)
            }));

            let postData: any = {
                commonSettings: {
                    commonSettingsId: commonSettings.commonSettingsId,
                    currencyCode: commonSettings.currencyCode,
                    referrermaxEarnings: convertToNumber(commonSettings.referrermaxEarnings),
                    defaultReferrerBonus: convertToNumber(commonSettings.defaultReferrerBonus),
                    defaultRefereeBonus: convertToNumber(commonSettings.defaultRefereeBonus),
                    refereeMinTxn: convertToNumber(commonSettings.refereeMinTxn)
                },
            };

            if (selectedRuleName !== "" && this.validateFields()) {
                postData.rules = {
                    ruleId: selectedRuleId,
                    ruleName: selectedRuleName,
                    ruleDescription: selectedRuleDescription,
                    pairs: formattedEntries
                };
            }

            const response = await ReferralService.updateAndSaveSettings(accessToken, postData);

            if (response.ok) {
                ShowAlert("Success", "Referral settings saved successfully.", "success")
                    .then(() => { this.getInitialData() })
            }
        } catch (error) {
            console.error("Error saving referral settings:", error);
            ShowAlert("Error", "An unexpected error occurred while saving referral settings.", "error");
        }
    };

    handleRuleSelection = (event: any) => {

        const selectedValue = event.target.value;
        const selectedRule = this.state.allRules.find(rule => rule.ruleName === selectedValue);

        this.setState((prevState) => ({
            selectedRuleId: selectedRule?.ruleId || "",
            selectedRuleName: selectedValue,
            selectedRuleDescription: selectedRule?.ruleDescription || "",
            selectedRule: selectedRule || null,
            rulePairs: selectedRule?.pairs || [],
            errors: {
                ...prevState.errors,
                ruleName: "", // Clear rule name error
            }
        }), () => this.validateFields());
    };

    render() {
        const { open, onClose } = this.props;
        const {
            commonSettings,
            referrerMaxEarningsList,
            rulePairs,
            isSaveConfirmationDialogOpen,
            selectedRule,
        } = this.state;
        return (
            <Box>
                <FormGroup>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <fieldset className="fieldset-border">
                            <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>Global settings</FormLabel>
                            <Box style={{ display: 'flex', gap: '10px', marginBottom: "20px" }}>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <FormLabel className='input-field-label'>Referrer currency</FormLabel>
                                        <Autocomplete
                                            options={referrerMaxEarningsList}
                                            getOptionLabel={(option) =>
                                                `${option.countryName || "-"} (${option.currencyCode || "-"})`
                                            }
                                            value={referrerMaxEarningsList.find(
                                                (option) => option.currencyCode === commonSettings.currencyCode
                                            ) || null}
                                            onChange={(event, newValue) => {
                                                this.handleCommonReferrerMaxLimit({
                                                    target: {
                                                        value: newValue ? newValue.currencyCode : ""
                                                    }
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    color="secondary"
                                                    variant="outlined"
                                                    fullWidth
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <MenuItem {...props}>
                                                    {option.countryName || "-"} ({option.currencyCode || "-"})
                                                </MenuItem>
                                            )}
                                        />
                                    </FormGroup>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <InputTextField
                                            label="Referrer max earning limit"
                                            value={commonSettings.referrermaxEarnings}
                                            startAdornment={commonSettings.currencySymbol}
                                            inputType="number"
                                            onChange={(value) => this.handleCommonLimitChange('referrermaxEarnings', value)}
                                            onBlur={() => this.handleCommonLimitBlur('referrermaxEarnings')}
                                            onFocus={() => this.handleCommonLimitFocus('referrermaxEarnings')}
                                        />
                                    </FormGroup>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <InputTextField
                                            label="Default referrer bonus"
                                            value={commonSettings.defaultReferrerBonus}
                                            startAdornment={commonSettings.currencySymbol}
                                            inputType="number"
                                            onChange={(value) => this.handleCommonLimitChange('defaultReferrerBonus', value)}
                                            onBlur={() => this.handleCommonLimitBlur('defaultReferrerBonus')}
                                            onFocus={() => this.handleCommonLimitFocus('defaultReferrerBonus')}
                                        />
                                    </FormGroup>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <InputTextField
                                            label="Default referee bonus"
                                            value={commonSettings.defaultRefereeBonus}
                                            startAdornment={commonSettings.currencySymbol}
                                            inputType="number"
                                            onChange={(value) => this.handleCommonLimitChange('defaultRefereeBonus', value)}
                                            onBlur={() => this.handleCommonLimitBlur('defaultRefereeBonus')}
                                            onFocus={() => this.handleCommonLimitFocus('defaultRefereeBonus')}
                                        />
                                    </FormGroup>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <InputTextField
                                            label="Referee min transaction"
                                            value={commonSettings.refereeMinTxn}
                                            startAdornment={commonSettings.currencySymbol}
                                            inputType="number"
                                            onChange={(value) => this.handleCommonLimitChange('refereeMinTxn', value)}
                                            onBlur={() => this.handleCommonLimitBlur('refereeMinTxn')}
                                            onFocus={() => this.handleCommonLimitFocus('refereeMinTxn')}
                                        />
                                    </FormGroup>
                                </Box>
                            </Box>
                        </fieldset>
                        <fieldset className="fieldset-border">
                            <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>Set attributes in currency pair rules</FormLabel>
                            <Box style={{ display: 'flex', gap: '10px', marginBottom: "20px" }}>
                                <Box display="flex" alignItems="flex-end" gap={2} style={{ flex: 1 }}>
                                    <Box style={{ flex: 1 }}>
                                        <FormGroup>
                                            <FormLabel className="input-field-label">Select by rule name</FormLabel>
                                            <FormControl fullWidth error={Boolean(this.state.errors?.ruleName)}>
                                                <Select
                                                    color="secondary"
                                                    variant="outlined"
                                                    value={this.state.selectedRuleName}
                                                    onChange={this.handleRuleSelection}
                                                    displayEmpty
                                                    renderValue={(selected) => {
                                                        if (!selected) {
                                                            return <>Select Rule</>;
                                                        }
                                                        return selected;
                                                    }}
                                                >
                                                    <MenuItem
                                                        value=""
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (this.props.goToTab) {
                                                                this.props.goToTab(1);
                                                            }
                                                        }}
                                                        sx={{ fontWeight: 'bold' }}
                                                    >
                                                        + Create rule
                                                    </MenuItem>

                                                    {/* Divider */}
                                                    <MenuItem disabled>
                                                        <hr style={{ width: '100%', borderColor: '#ccc' }} />
                                                    </MenuItem>

                                                    {/* Existing Rules */}
                                                    {this.state.allRules.map((rule, index) => (
                                                        <MenuItem key={index} value={rule.ruleName}>
                                                            {rule.ruleName} - ({rule.corridorCurrency})
                                                        </MenuItem>
                                                    ))}
                                                </Select>

                                                {this.state.errors?.ruleName && (
                                                    <FormHelperText>{this.state.errors.ruleName}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </FormGroup>
                                    </Box>
                                    {selectedRule ? (
                                        <>
                                            <Box style={{ flex: 1 }}>
                                                <FormGroup>
                                                    <FormLabel className='input-field-label'>Corridor country</FormLabel>
                                                    <Typography variant="body1" sx={{
                                                        padding: '16px 0',
                                                        //borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                                        fontWeight: "bold"
                                                    }}>
                                                        {selectedRule.corridorCountryName || "-"} ({selectedRule.corridorCurrency || "-"})
                                                    </Typography>
                                                </FormGroup>
                                            </Box>
                                            <Box style={{ flex: 2 }}>
                                                <FormGroup>
                                                    <FormLabel className='input-field-label'>Rule Description</FormLabel>
                                                    <Typography variant="body1" sx={{
                                                        padding: '16px 0',
                                                        //borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                                        fontWeight: "bold"
                                                    }}>
                                                        {selectedRule.ruleDescription || '—'}
                                                    </Typography>
                                                </FormGroup>
                                            </Box>
                                        </>
                                    ) : (
                                        <>
                                            <Box style={{ flex: 1 }}></Box>
                                            <Box style={{ flex: 2 }}></Box>
                                        </>
                                    )}
                                </Box>
                            </Box>
                            {rulePairs.map((entry, index) => this.renderReferralEntry(entry, index))}
                        </fieldset>
                    </Box>
                </FormGroup>
                <AlertDialogPopop
                    open={isSaveConfirmationDialogOpen}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to save the settings?`}
                    onClose={(state_, action_) => {
                        if (action_ === "ok") {
                            this.handleSave();
                        }
                        this.setState({
                            isSaveConfirmationDialogOpen: false,
                        });
                    }}
                />
            </Box>
        );
    }

    // Render the referral currency pair feilds...
    renderReferralEntry = (entry: RulePair, index: number) => {
        const {
            referrerMaxEarningsList,
            errors,
            rulePairs
        } = this.state;

        const rowStyle = {
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            marginBottom: 10
        };

        const inputBoxStyle = { flex: 1 };

        if (!this.entryRefs[index]) {
            this.entryRefs[index] = React.createRef();
        }

        return (
            <Box
                key={index} ref={this.entryRefs[index]}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: 20,
                }}
            >
                <fieldset className="fieldset-border" style={{ flex: 1 }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        {/* Pair title */}
                        {`Pair ${index + 1}, ${entry.refereeCountryName && entry.corridorCountryName
                            ? `${entry.refereeCountryName} to ${entry.corridorCountryName}`
                            : ''
                            }`}
                    </FormLabel>
                    <Box style={rowStyle}>
                        <Box style={inputBoxStyle}>
                            <FormGroup>
                                <FormLabel className='input-field-label'>Referee country</FormLabel>
                                <Typography variant="body1" sx={{
                                    padding: '16px 0',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                    fontWeight: "bold"
                                }}>
                                    {entry.refereeCountryName || "-"} ({entry.refereeCurrency || "-"})
                                </Typography>
                            </FormGroup>
                        </Box>
                        <Box style={inputBoxStyle}>
                            <InputTextField
                                label="Referee Min. transaction"
                                //tooltipText="To be eligible for the bonus for both the referrer and referee, the referee's first transaction must be successful and must meet or exceed the referrer’s minimum transaction amount, which is calculated based on the referrer’s own currency."
                                value={entry.refereeMinTransaction}
                                startAdornment={entry.refereeCurrencySymbol?? "$"}
                                inputType="number"
                                onChange={(value) => this.handleBonusAndMinTrans('refereeMinTransaction', index, value)}
                                onBlur={() => this.handleBonusAndMinTransBlur('refereeMinTransaction', index)}
                                onFocus={() => this.handleBonusAndMinTransFocus('refereeMinTransaction', index)}
                                error={Boolean(errors?.rulePairs?.[index]?.refereeMinTransaction)}
                                helperText={errors?.rulePairs?.[index]?.refereeMinTransaction || ""}
                            />
                        </Box>
                        <Box style={inputBoxStyle}>
                            <InputTextField
                                label="Referee bonus credit"
                                //tooltipText="If eligible, the referee will receive a bonus amount equivalent to the value specified in the referrer’s currency."
                                value={entry.refereeBonusCredit}
                                startAdornment={entry.refereeCurrencySymbol?? "$"}
                                inputType="number"
                                onChange={(value) => this.handleBonusAndMinTrans('refereeBonusCredit', index, value)}
                                onBlur={() => this.handleBonusAndMinTransBlur('refereeBonusCredit', index)}
                                onFocus={() => this.handleBonusAndMinTransFocus('refereeBonusCredit', index)}
                                error={Boolean(errors?.rulePairs?.[index]?.refereeBonusCredit)}
                                helperText={errors?.rulePairs?.[index]?.refereeBonusCredit || ""}
                            />
                        </Box>
                        <Box style={inputBoxStyle}>
                            <InputTextField
                                label="Referrer bonus credit"
                                //tooltipText="If eligible, the referrer will receive a bonus amount equivalent to the value specified in the referrer’s currency."
                                value={entry.referrerBonusCredit}
                                startAdornment={entry.refereeCurrencySymbol ?? "$"}
                                inputType="number"
                                onChange={(value) => this.handleBonusAndMinTrans('referrerBonusCredit', index, value)}
                                onBlur={() => this.handleBonusAndMinTransBlur('referrerBonusCredit', index)}
                                onFocus={() => this.handleBonusAndMinTransFocus('referrerBonusCredit', index)}
                                error={Boolean(errors?.rulePairs?.[index]?.referrerBonusCredit)}
                                helperText={errors?.rulePairs?.[index]?.referrerBonusCredit || ""}
                            />
                        </Box>
                    </Box>
                </fieldset>
            </Box >
        );
    };

    // Get the rule name and Description from the users...
    handleRuleTextChange = <K extends keyof AddReferralBonusCreditsState>(field: K) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const maxLengths: Partial<Record<keyof AddReferralBonusCreditsState, number>> = {
            ruleName: 40,
            ruleDescription: 100,
        };

        const rawValue = event.target.value;
        const maxLength = maxLengths[field];
        let value = maxLength ? rawValue.slice(0, maxLength) : rawValue;

        // Capitalize the first character
        value = value.charAt(0).toUpperCase() + value.slice(1);

        this.setState(prevState => ({
            ...prevState,
            [field]: value,
            errors: {
                ...prevState.errors,
                [field]: "" // Clear error for this field when user types
            }
        }));
    };

    // handle events for referrer maximun potential earnings....
    handleCommonReferrerMaxLimit = (event: any) => {
        const selectedCurrency = event.target.value;

        const selectedSenderCurrency = this.state.referrerMaxEarningsList.find(
            (item) => item.currencyCode === selectedCurrency
        );

        if (selectedSenderCurrency) {
            this.setState({
                commonSettings: selectedSenderCurrency
            });
        }
    };

    // handle bonus inputs for referrer and referee...
    handleBonusAndMinTrans = (
        field: 'referrerBonusCredit' | 'refereeCurrency' | 'refereeBonusCredit' | 'refereeMinTransaction',
        index: number,
        value: string
    ) => {
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999;

        // Allow only one decimal point
        const decimalCount = (sanitizedValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
            // Keep only part up to the first decimal
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        this.setState((prevState) => {
            const updatedEntries = [...prevState.rulePairs];
            updatedEntries[index][field] = sanitizedValue;

            const updatedErrors = { ...prevState.errors };
            if (
                updatedErrors.rulePairs &&
                updatedErrors.rulePairs[index] &&
                Object.prototype.hasOwnProperty.call(updatedErrors.rulePairs[index], field)
            ) {
                updatedErrors.rulePairs[index][field] = ""; // clear specific error
            }

            return {
                rulePairs: updatedEntries,
                errors: updatedErrors,
            };
        });
    };

    // handle events for referrer and refree inputs on blur...
    handleBonusAndMinTransBlur = (field: keyof RulePair, index: number) => {
        this.setState((prevState: Readonly<AddReferralBonusCreditsState>) => {
            const updatedEntries = [...prevState.rulePairs];
            const value = updatedEntries[index][field];
            const raw = typeof value === 'string' && value ? value.replace(/,/g, '') : '0';

            const updatedAmount = amountFormat(
                parseFloat(raw),
                "en-US",
                "USD"
            ).replace("$", "");

            updatedEntries[index] = {
                ...updatedEntries[index],
                [field]: updatedAmount
            };

            return { rulePairs: updatedEntries };
        });
    };

    // handle events for referrer and refree inputs on focus in to number...
    handleBonusAndMinTransFocus = (field: keyof RulePair, index: number) => {
        this.setState((prevState: Readonly<AddReferralBonusCreditsState>) => {
            const updatedEntries = [...prevState.rulePairs];
            const value = updatedEntries[index][field];
            if (typeof value === 'string') {
                const numeric = value.replace(/,/g, "");
                updatedEntries[index] = {
                    ...updatedEntries[index],
                    [field]: numeric
                };
            }
            return { rulePairs: updatedEntries };
        });
    };

    // handle common settings change...
    handleCommonLimitChange = (field: string, value: string) => {
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999;

        // Allow only one decimal point
        const decimalCount = (sanitizedValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
            // If there are multiple decimal points, remove everything after the first one
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        // Restrict max limit
        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        this.setState((prevState) => ({
            ...prevState,
            commonSettings: {
                ...prevState.commonSettings,
                [field]: sanitizedValue,
            },
        }));
    };

    // handle common settings change...
    handleCommonLimitBlur = (field: keyof typeof this.state.commonSettings) => {
        const value = this.state.commonSettings[field];
        const n_ = typeof value === 'string' && value ? value : "0";

        const updatedAmount = amountFormat(
            parseFloat(n_.replace(/,/g, "")),
            "en-US",
            "USD"
        ).replace("$", "");

        this.setState((prevState) => ({
            ...prevState,
            commonSettings: {
                ...prevState.commonSettings,
                [field]: updatedAmount,
            },
            [`${field}InputType`]: "text",
        }));
    };

    // handle common settings change...
    handleCommonLimitFocus = (field: keyof typeof this.state.commonSettings) => {
        const value = this.state.commonSettings[field];
        if (typeof value === 'string') {
            const n_ = value.replace(/,/g, "");
            this.setState((prevState) => ({
                ...prevState,
                commonSettings: {
                    ...prevState.commonSettings,
                    [field]: n_,
                },
                [`${field}InputType`]: "number"
            }));
        }
    };
}

export default AddReferralBonusCredits;